import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Send, Trash2 } from "lucide-react";
import { isDecimal, fmt, round, Variance } from "./reconUtils";
import SubmitDialog from "./SubmitDialog";

const counted = (v) => v !== "" && v !== undefined && v !== null && !Number.isNaN(Number(v));

export default function CountSheet({ storeName, onChanged }) {
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({});
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    const [p, r] = await Promise.all([api.get("/products"), api.get("/inventory/reconciliations")]);
    setProducts(p.data.filter((x) => x.active));
    const d = r.data.find((x) => x.status === "draft") || null;
    setDraft(d);
    setCounts(d ? Object.fromEntries(d.variances.map((v) => [v.product_id, String(v.counted_qty)])) : {});
    setNotes(d?.notes || "");
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const draftSys = useMemo(() => Object.fromEntries((draft?.variances || []).map((v) => [v.product_id, v])), [draft]);
  const items = Object.entries(counts).filter(([id, v]) => counted(v) && byId[id])
    .map(([product_id, v]) => ({ product_id, counted_qty: Number(v) }));
  const lines = items.map((i) => {
    const p = byId[i.product_id];
    const snap = draftSys[i.product_id];
    const sys = snap && Number(snap.counted_qty) === i.counted_qty ? snap.system_qty : p.quantity_on_hand;
    return { ...i, name: p.name, sku: p.sku, unit: p.unit, system_qty: sys, variance: round(i.counted_qty - sys) };
  });
  const invalid = lines.filter((l) => l.counted_qty < 0 || (!isDecimal(byId[l.product_id]) && !Number.isInteger(l.counted_qty)));
  const shown = products.filter((p) => !search || `${p.name} ${p.sku} ${p.category}`.toLowerCase().includes(search.toLowerCase()));

  const save = async () => {
    const r = draft ? await api.put(`/inventory/reconcile/drafts/${draft.id}`, { items, notes })
      : await api.post("/inventory/reconcile/drafts", { items, notes });
    setDraft(r.data);
    return r.data;
  };
  const run = async (fn, ok, validate = true) => {
    if (validate && invalid.length) return toast.error(`Fix ${invalid.length} invalid count(s): whole units only for packaged products, no negatives`);
    setBusy(true);
    try { await fn(); toast.success(ok); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  const submit = () => run(async () => {
    const d = await save();
    await api.post(`/inventory/reconcile/${d.id}/submit`);
    setConfirm(false); setDraft(null); setCounts({}); setNotes(""); onChanged?.(); await load();
  }, "Count submitted for management approval — stock not changed yet");
  const discard = () => run(async () => {
    await api.delete(`/inventory/reconcile/drafts/${draft.id}`);
    setDraft(null); setCounts({}); setNotes("");
  }, "Draft discarded", false);

  return (
    <Card className="p-4 border-border shadow-sm space-y-3" data-testid="recon-count-sheet">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          Counting <span className="font-semibold" data-testid="recon-store-name">{storeName}</span>
          {draft && <span className="text-muted-foreground" data-testid="recon-draft-indicator"> · draft saved {new Date(draft.updated_at || draft.at).toLocaleString("en-ZA")}</span>}
        </div>
        <Input placeholder="Search product, SKU or category" value={search} onChange={(e) => setSearch(e.target.value)}
          className="h-9 w-full sm:w-72" data-testid="recon-search" />
      </div>
      <p className="text-xs text-muted-foreground">Leave a field empty if the product was not counted. Enter 0 only if none are on hand. Saving or submitting never changes stock.</p>
      <div className="overflow-auto thin-scroll max-h-[55vh] border rounded-md">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-background z-10 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left p-2">Product</th><th className="text-right p-2">System</th><th className="text-right p-2 w-24 sm:w-32">Physical</th><th className="text-right p-2">Variance</th></tr>
          </thead>
          <tbody>
            {shown.map((p) => {
              const v = counts[p.id];
              const line = lines.find((l) => l.product_id === p.id);
              const bad = invalid.some((l) => l.product_id === p.id);
              return (
                <tr key={p.id} className="border-t" data-testid={`recon-row-${p.sku}`}>
                  <td className="p-2 min-w-[9rem]"><div className="line-clamp-2">{p.name}</div><div className="font-mono text-[11px] text-muted-foreground">{p.sku} · {p.category} · {p.unit}</div></td>
                  <td className="p-2 text-right font-mono text-xs" data-testid={`recon-system-${p.sku}`}>{fmt(line ? line.system_qty : p.quantity_on_hand)}</td>
                  <td className="p-2 text-right">
                    <Input data-testid={`recon-count-${p.sku}`} type="number" inputMode="decimal" min="0"
                      step={isDecimal(p) ? "0.01" : "1"} placeholder="—" value={v ?? ""}
                      className={`h-8 w-24 sm:w-32 ml-auto px-2 text-right font-mono [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${bad ? "border-red-500" : ""}`}
                      onChange={(e) => setCounts((c) => ({ ...c, [p.id]: e.target.value }))} />
                  </td>
                  <td className="p-2 text-right text-xs"><Variance value={line ? line.variance : null} testid={`recon-variance-${p.sku}`} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Textarea placeholder="Count notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="recon-notes" />
      <div className="flex flex-wrap justify-end gap-2">
        {draft && <Button variant="ghost" className="text-[#C05746]" disabled={busy} onClick={discard} data-testid="recon-discard-draft-btn"><Trash2 className="w-4 h-4 mr-1" />Discard draft</Button>}
        <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => run(save, "Draft saved — stock not changed")} data-testid="recon-save-draft-btn"><Save className="w-4 h-4 mr-1" />Save draft</Button>
        <Button className="rounded-full" disabled={busy || !lines.length} onClick={() => (invalid.length ? toast.error(`Fix ${invalid.length} invalid count(s) before submitting`) : setConfirm(true))} data-testid="recon-submit-btn"><Send className="w-4 h-4 mr-1" />Submit for approval ({lines.length})</Button>
      </div>
      <SubmitDialog open={confirm} onOpenChange={setConfirm} lines={lines} busy={busy} onConfirm={submit} storeName={storeName} />
    </Card>
  );
}
